import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './TrocasAdmin.css';

/**
 * Página para Admin realizar trocas em nome dos jogadores
 */

const TrocasAdmin = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jogadores, setJogadores] = useState([]);
  const [jogadorSelecionado, setJogadorSelecionado] = useState(null);
  const [nocautesJogador, setNocautesJogador] = useState(null);
  const [trocas, setTrocas] = useState([]);
  const [rodadasAbertas, setRodadasAbertas] = useState([]);
  const [historicoTrocas, setHistoricoTrocas] = useState([]);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [jogadorHistorico, setJogadorHistorico] = useState(null);
  const [trocaAtual, setTrocaAtual] = useState({
    tipo_troca: '',
    quantidade: 1,
    rodada_id: '',
  });

  useEffect(() => {
    if (!isAdmin) {
      setError('Acesso negado. Apenas administradores podem acessar esta página.');
      return;
    }
    carregarDados();
  }, [isAdmin]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');
      const [jogadoresRes, trocasRes, rodadasRes] = await Promise.all([
        apiClient.get('/jogadores'),
        apiClient.get('/nocautes/trocas'),
        apiClient.get('/rodadas'),
      ]);

      setJogadores(jogadoresRes.data.filter(j => !j.is_deleted && j.ativo));
      setTrocas(trocasRes.data);
      setRodadasAbertas(rodadasRes.data.filter(r => r.status === 'aberta'));
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const carregarNocautesJogador = async (jogadorId) => {
    try {
      setError('');
      const response = await apiClient.get(`/nocautes/jogador/${jogadorId}`);
      setNocautesJogador(response.data);
    } catch (err) {
      console.error('Erro ao carregar nocautes do jogador:', err);
      setError(err.response?.data?.error || 'Erro ao carregar nocautes do jogador');
    }
  };

  const carregarHistorico = async (jogadorId = null) => {
    try {
      const params = jogadorId ? { jogador_id: jogadorId } : {};
      const response = await apiClient.get('/nocautes/historico', { params });
      setHistoricoTrocas(response.data);
      setJogadorHistorico(jogadorId);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setError(err.response?.data?.error || 'Erro ao carregar histórico');
    }
  };

  const handleSelecionarJogador = async (jogadorId) => {
    const jogador = jogadores.find(j => j.id === jogadorId);
    setJogadorSelecionado(jogador);
    await carregarNocautesJogador(jogadorId);
    setTrocaAtual({ tipo_troca: '', quantidade: 1, rodada_id: '' });
  };

  const handleTrocar = async (e) => {
    e.preventDefault();
    setError('');

    if (!jogadorSelecionado) {
      setError('Selecione um jogador primeiro');
      return;
    }

    if (!trocaAtual.tipo_troca) {
      setError('Selecione um tipo de troca');
      return;
    }

    try {
      const payload = {
        jogador_id: jogadorSelecionado.id,
        tipo_troca: trocaAtual.tipo_troca,
      };

      if (trocaAtual.tipo_troca === 'fichas') {
        const quantidadeInput = parseInt(trocaAtual.quantidade);
        
        // Validar que a quantidade seja pelo menos 1
        if (!quantidadeInput || quantidadeInput < 1) {
          setError('A quantidade de nocautes para trocar por fichas deve ser no mínimo 1.');
          return;
        }
        
        // Validar que o jogador tenha pelo menos 1 nocaute disponível
        if (nocautesDisponiveis < 1) {
          setError('O jogador precisa ter pelo menos 1 nocaute disponível para realizar trocas.');
          return;
        }
        
        // Validar que a quantidade não seja maior que os disponíveis
        if (quantidadeInput > nocautesDisponiveis) {
          setError(`O jogador tem apenas ${nocautesDisponiveis} nocautes disponíveis. Não é possível trocar ${quantidadeInput} nocautes.`);
          return;
        }
        
        payload.quantidade = quantidadeInput;
      }

      if (trocaAtual.tipo_troca === 'buyin') {
        if (!trocaAtual.rodada_id) {
          setError('Selecione uma rodada aberta para usar buy-in dobrado');
          return;
        }
        payload.rodada_id = trocaAtual.rodada_id;
      }

      const response = await apiClient.post('/nocautes/trocar-admin', payload);
      
      alert(response.data.mensagem || response.data.message || 'Troca realizada com sucesso!');
      setTrocaAtual({ tipo_troca: '', quantidade: 1, rodada_id: '' });
      await carregarNocautesJogador(jogadorSelecionado.id);
      if (mostrarHistorico) {
        await carregarHistorico(jogadorSelecionado.id);
      }
    } catch (err) {
      console.error('Erro ao realizar troca:', err);
      setError(err.response?.data?.error || 'Erro ao realizar troca');
    }
  };

  if (!isAdmin) {
    return <div className="container">Acesso negado. Apenas administradores podem acessar esta página.</div>;
  }

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  const nocautesDisponiveis = nocautesJogador?.nocautes_dados?.disponiveis || 0;

  return (
    <div className="container trocas-admin-page">
      <div className="page-header tolkien-admin-trocas-theme">
        <div className="theme-header-admin-trocas">
          <span className="tolkien-admin-icon">👑</span>
          <h1>Trocas - Admin</h1>
          <span className="tolkien-admin-icon">👑</span>
        </div>
        <p className="theme-subtitle-admin-trocas">"Realize trocas em nome dos jogadores"</p>
        <div className="admin-actions-header">
          <button 
            className="btn btn-secondary btn-historico" 
            onClick={() => {
              if (!mostrarHistorico) {
                carregarHistorico();
              }
              setMostrarHistorico(!mostrarHistorico);
            }}
          >
            {mostrarHistorico ? 'Ocultar' : 'Ver'} Histórico de Trocas
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Histórico de Trocas */}
      {mostrarHistorico && (
        <div className="historico-trocas-card">
          <h2>📜 Histórico de Trocas {jogadorHistorico ? '(Filtrado)' : '(Todos os Jogadores)'}</h2>
          {jogadorSelecionado && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => carregarHistorico(jogadorSelecionado.id)}
              style={{ marginBottom: '15px' }}
            >
              Ver histórico de {jogadorSelecionado.nome}
            </button>
          )}
          {historicoTrocas.length === 0 ? (
            <p className="no-data">Nenhuma troca encontrada.</p>
          ) : (
            <div className="historico-lista">
              {historicoTrocas.map((troca) => (
                <div key={troca.id} className="historico-item">
                  <div className="historico-header">
                    <div className="historico-info">
                      <div className="historico-tipo">
                        <strong>{troca.tipo_troca}</strong>
                      </div>
                      <div className="historico-jogador">
                        <strong>Jogador:</strong> {troca.jogador?.nome || 'N/A'}
                      </div>
                    </div>
                    <div className="historico-data">
                      {new Date(troca.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  <div className="historico-detalhes">
                    <p><strong>Nocautes usados:</strong> {troca.quantidade_nocautes}</p>
                    {troca.quantidade_fichas && (
                      <p><strong>Fichas ganhas:</strong> {troca.quantidade_fichas.toLocaleString('pt-BR')}</p>
                    )}
                    {troca.rodada && (
                      <p><strong>Rodada:</strong> {new Date(troca.rodada.data).toLocaleDateString('pt-BR')}</p>
                    )}
                    {troca.observacoes && (
                      <p><strong>Observações:</strong> {troca.observacoes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="admin-trocas-content">
        {/* Seleção de Jogador */}
        <div className="selecao-jogador-card">
          <h2>Selecionar Jogador</h2>
          <div className="form-group">
            <label>Jogador *</label>
            <select
              value={jogadorSelecionado?.id || ''}
              onChange={(e) => handleSelecionarJogador(e.target.value)}
              required
            >
              <option value="">Selecione um jogador...</option>
              {jogadores.map(jogador => (
                <option key={jogador.id} value={jogador.id}>
                  {jogador.nome} ({jogador.email})
                </option>
              ))}
            </select>
          </div>

          {jogadorSelecionado && nocautesJogador && (
            <div className="nocautes-info-card">
              <h3>Nocautes de {jogadorSelecionado.nome}</h3>
              <div className="nocautes-stats-admin">
                <div className="stat-item-admin">
                  <span className="stat-label">Total:</span>
                  <span className="stat-value">{nocautesJogador.nocautes_dados.total}</span>
                </div>
                <div className="stat-item-admin">
                  <span className="stat-label">Disponíveis:</span>
                  <span className="stat-value disponivel">{nocautesDisponiveis}</span>
                </div>
                <div className="stat-item-admin">
                  <span className="stat-label">Usados em Trocas:</span>
                  <span className="stat-value">{nocautesJogador.nocautes_dados.usados_em_trocas || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Formulário de Troca */}
        {jogadorSelecionado && (
          <div className="formulario-troca-admin-card">
            <h2>Realizar Troca para {jogadorSelecionado.nome}</h2>
            
            {nocautesDisponiveis === 0 && (
              <div className="warning-card">
                <p>⚠️ Este jogador não tem nocautes disponíveis para trocar.</p>
              </div>
            )}

            <form onSubmit={handleTrocar} className="form-trocas-admin">
              <div className="form-group">
                <label>Tipo de Troca *</label>
                <select
                  value={trocaAtual.tipo_troca}
                  onChange={(e) => setTrocaAtual({ ...trocaAtual, tipo_troca: e.target.value })}
                  required
                  disabled={nocautesDisponiveis === 0}
                >
                  <option value="">Selecione uma troca...</option>
                  {trocas.map(troca => (
                    <option key={troca.tipo} value={troca.tipo}>
                      {troca.descricao}
                    </option>
                  ))}
                </select>
              </div>

              {trocaAtual.tipo_troca && (() => {
                const trocaSelecionada = trocas.find(t => t.tipo === trocaAtual.tipo_troca);
                if (!trocaSelecionada) return null;

                return (
                  <>
                    <div className="troca-info">
                      <p><strong>Benefício:</strong> {trocaSelecionada.nome}</p>
                      <p><strong>Nocautes necessários:</strong> {trocaSelecionada.quantidade === 'variável' ? 'X (você define)' : trocaSelecionada.quantidade}</p>
                      {trocaSelecionada.quantidade !== 'variável' && nocautesDisponiveis < trocaSelecionada.quantidade && (
                        <p className="warning-text">
                          ⚠️ O jogador precisa de {trocaSelecionada.quantidade} nocautes, mas tem apenas {nocautesDisponiveis}
                        </p>
                      )}
                    </div>

                    {trocaAtual.tipo_troca === 'fichas' && (
                      <div className="form-group">
                        <label>Quantidade de Nocautes *</label>
                        <input
                          type="number"
                          min="1"
                          max={nocautesDisponiveis}
                          value={trocaAtual.quantidade}
                          onChange={(e) => {
                            const valor = parseInt(e.target.value) || 0;
                            // Garantir que o valor seja pelo menos 1
                            const valorFinal = valor < 1 ? 1 : (valor > nocautesDisponiveis ? nocautesDisponiveis : valor);
                            setTrocaAtual({ ...trocaAtual, quantidade: valorFinal });
                          }}
                          required
                          disabled={nocautesDisponiveis < 1}
                        />
                        <small>
                          O jogador receberá {trocaAtual.quantidade * 1000} fichas ({trocaAtual.quantidade} nocautes × 1.000 fichas)
                          {nocautesDisponiveis < 1 && (
                            <span className="warning-text"> ⚠️ O jogador precisa ter pelo menos 1 nocaute disponível.</span>
                          )}
                        </small>
                      </div>
                    )}

                    {trocaAtual.tipo_troca === 'buyin' && (
                      <div className="form-group">
                        <label>Rodada Aberta *</label>
                        <select
                          value={trocaAtual.rodada_id}
                          onChange={(e) => setTrocaAtual({ ...trocaAtual, rodada_id: e.target.value })}
                          required
                        >
                          <option value="">Selecione uma rodada...</option>
                          {rodadasAbertas.map(rodada => (
                            <option key={rodada.id} value={rodada.id}>
                              {new Date(rodada.data).toLocaleDateString('pt-BR')}
                            </option>
                          ))}
                        </select>
                        {rodadasAbertas.length === 0 && (
                          <small className="warning-text">Não há rodadas abertas no momento</small>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setJogadorSelecionado(null);
                    setNocautesJogador(null);
                    setTrocaAtual({ tipo_troca: '', quantidade: 1, rodada_id: '' });
                  }}
                >
                  Limpar Seleção
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={!trocaAtual.tipo_troca || nocautesDisponiveis < 1 || (() => {
                    const trocaSelecionada = trocas.find(t => t.tipo === trocaAtual.tipo_troca);
                    if (trocaAtual.tipo_troca === 'fichas') {
                      const quantidadeInput = parseInt(trocaAtual.quantidade) || 0;
                      return quantidadeInput < 1 || quantidadeInput > nocautesDisponiveis;
                    }
                    return trocaSelecionada && trocaSelecionada.quantidade !== 'variável' && nocautesDisponiveis < trocaSelecionada.quantidade;
                  })()}
                >
                  Confirmar Troca
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrocasAdmin;
